import { ThemeEvents } from '@theme/events';

export function normalizeDrainLength(value, unit = 'mm', maxLengthMm = 5600) {
  const text = String(value).trim();
  let lengthMm;
  if (unit === 'mm' && /^\d+$/.test(text)) lengthMm = Number(text);
  else if (unit === 'm' && /^\d+(?:\.\d{1,3})?$/.test(text)) {
    const [whole, fraction = ''] = text.split('.');
    lengthMm = Number(whole) * 1000 + Number(fraction.padEnd(3, '0'));
  }
  if (!Number.isSafeInteger(lengthMm) || lengthMm < 1 || lengthMm > maxLengthMm) throw new RangeError('length');
  return lengthMm;
}

export function calculateDrainEstimate(rateCents, lengthMm, pieces) {
  if (!Number.isSafeInteger(rateCents) || rateCents <= 0) throw new RangeError('variant');
  if (!Number.isSafeInteger(lengthMm) || lengthMm < 1 || lengthMm > 5600) throw new RangeError('length');
  if (!Number.isSafeInteger(pieces) || pieces < 1 || pieces > 999999) throw new RangeError('pieces');
  const totalMm = lengthMm * pieces;
  const numerator = rateCents * totalMm;
  if (!Number.isSafeInteger(numerator)) throw new RangeError('pieces');
  return { totalMm, totalCents: Math.round(numerator / 1000) };
}

const BaseElement = globalThis.HTMLElement || class {};

export class LinearDrainCalculator extends BaseElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.form = this.querySelector('form');
    this.lengthInput = this.querySelector('[data-drain-length]');
    this.unitGroup = this.querySelector('[data-drain-unit-group]');
    this.unitInputs = this.querySelectorAll('[data-drain-unit]');
    this.unitInput = this.querySelector('[data-drain-unit]:checked');
    this.piecesInput = this.querySelector('[data-drain-pieces]');
    this.variantInput = this.querySelector('[data-drain-variant]');
    this.error = this.querySelector('[data-drain-error]');
    this.submitButton = this.querySelector('[type="submit"]');
    try {
      this.config = JSON.parse(this.querySelector('[data-drain-config]').textContent);
      if (!Array.isArray(this.config.variants)) throw new Error('variant');
      this.variant = this.config.variants.find((variant) => String(variant.id) === this.variantInput.value);
      this.money = new Intl.NumberFormat(this.config.locale || 'en-AU', { style: 'currency', currency: this.config.currency || 'AUD' });
      this.number = new Intl.NumberFormat(this.config.locale || 'en-AU', { maximumFractionDigits: 3 });
    } catch {
      this.submitButton.disabled = true;
      return;
    }
    this.controller = new AbortController();
    const listenerOptions = { signal: this.controller.signal };
    this.unitInputs.forEach((input) => { input.disabled = false; });
    this.previousUnit = this.unitInput.value;
    this.form.addEventListener('input', (event) => {
      if (!event.target.matches('[data-drain-unit]')) this.updateEstimate();
    }, listenerOptions);
    this.unitGroup.addEventListener('change', () => {
      this.unitInput = this.querySelector('[data-drain-unit]:checked');
      this.changeUnit();
    }, listenerOptions);
    this.form.addEventListener('submit', (event) => this.requestCut(event), listenerOptions);
    this.section = this.closest('.shopify-section, dialog');
    this.onVariantSelected = (event) => {
      const picker = event.target?.closest?.('variant-picker[data-product-id]');
      if (String(picker?.dataset.productId) !== String(this.config.productId)) return;
      this.variantPending = true;
      this.pendingOptions = Array.from(picker.querySelectorAll('select option[selected], fieldset input:checked')).map((option) => option.value);
      this.variant = null;
      this.variantInput.value = '';
      this.updateEstimate();
    };
    this.onVariantUpdate = (event) => {
      if (String(event.detail?.data?.productId) !== String(this.config.productId)) return;
      if (this.variantPending && event.detail.resource) {
        const options = event.detail.resource.options;
        if (!Array.isArray(options) || options.length !== this.pendingOptions.length || options.some((option, index) => option !== this.pendingOptions[index])) return;
      }
      this.variantPending = false;
      this.pendingOptions = null;
      this.variant = this.config.variants.find((variant) => String(variant.id) === String(event.detail.resource?.id));
      this.variantInput.value = this.variant ? String(this.variant.id) : '';
      this.updateEstimate();
    };
    this.section?.addEventListener(ThemeEvents.variantSelected, this.onVariantSelected, listenerOptions);
    this.section?.addEventListener(ThemeEvents.variantUpdate, this.onVariantUpdate, listenerOptions);
    this.updateEstimate();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.initialized = false;
  }

  readSelection() {
    if (this.variantPending || !this.variant) throw new RangeError('variant');
    const lengthMm = normalizeDrainLength(this.lengthInput.value, this.unitInput.value, this.config.maxLengthMm);
    if (!/^[1-9]\d{0,5}$/.test(this.piecesInput.value)) throw new RangeError('pieces');
    const pieces = Number(this.piecesInput.value);
    return { lengthMm, pieces, ...calculateDrainEstimate(this.variant.price, lengthMm, pieces) };
  }

  changeUnit() {
    try {
      const lengthMm = normalizeDrainLength(this.lengthInput.value, this.previousUnit, this.config.maxLengthMm);
      this.lengthInput.value = this.unitInput.value === 'm' ? String(lengthMm / 1000) : String(lengthMm);
    } catch {
      this.lengthInput.value = '';
    }
    const metres = this.unitInput.value === 'm';
    this.lengthInput.min = metres ? '0.001' : '1';
    this.lengthInput.max = metres ? '5.6' : '5600';
    this.lengthInput.step = metres ? '0.001' : '1';
    this.lengthInput.inputMode = metres ? 'decimal' : 'numeric';
    this.previousUnit = this.unitInput.value;
    this.updateEstimate();
  }

  updateEstimate() {
    this.lengthInput.setCustomValidity('');
    this.piecesInput.setCustomValidity('');
    this.querySelector('[data-drain-rate]').textContent = !this.variantPending && this.variant ? `${this.money.format(this.variant.price / 100)}/m` : '—';
    try {
      const selection = this.readSelection();
      this.querySelector('[data-drain-total-length]').textContent = `${this.number.format(selection.totalMm / 1000)} m`;
      this.querySelector('[data-drain-total]').textContent = this.money.format(selection.totalCents / 100);
      this.error.hidden = true;
      this.error.textContent = '';
      this.submitButton.disabled = false;
    } catch (error) {
      const field = ['length', 'pieces', 'variant'].includes(error.message) ? error.message : 'variant';
      const message = this.config.messages[field];
      this.error.textContent = message;
      this.error.hidden = false;
      if (field === 'length') this.lengthInput.setCustomValidity(message);
      if (field === 'pieces') this.piecesInput.setCustomValidity(message);
      this.querySelector('[data-drain-total]').textContent = '—';
      this.querySelector('[data-drain-total-length]').textContent = '—';
      this.submitButton.disabled = field === 'variant';
    }
  }

  requestCut(event) {
    event.preventDefault();
    this.updateEstimate();
    if (this.variantPending || !this.form.reportValidity() || !this.variant) return;
    const selection = this.readSelection();
    const url = new URL(this.config.rootUrl || '/', window.location.origin);
    url.search = new URLSearchParams({ view: 'delivery-quote', product: this.config.handle, variant: String(this.variant.id), length_mm: String(selection.lengthMm), quantity: String(selection.pieces) }).toString();
    window.location.assign(`${url.pathname}${url.search}`);
  }
}

if (globalThis.customElements && !customElements.get('linear-drain-calculator')) customElements.define('linear-drain-calculator', LinearDrainCalculator);
